import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { sieveAnalysis as api } from '@/api/endpoints';
import type {
  SieveProject, SieveSample, SieveMeta, SieveDecisionBasis, SieveDecisionInput,
  SieveResult, SieveKf, SieveKfMethod, SieveTerz, SieveReportOptions,
} from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { hasPermission } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { GrainCurveChart, type CurveMarker, type CurveBand } from '@/components/sieve/GrainCurveChart';
import { SlotScale, PackScale } from '@/components/sieve/ComparisonScales';
import { SieveProjectFormModal, sieveProjectToInput } from '@/components/sieve/SieveProjectFormModal';
import { MethodGuide } from '@/components/sieve/MethodGuide';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

const fmt = (x?: number | null, d = 2) => (x == null || Number.isNaN(x)) ? '–' : x.toFixed(d).replace('.', ',');
const num = (s: string) => { const v = parseFloat(String(s).replace(',', '.')); return Number.isNaN(v) ? 0 : v; };
const numOrNull = (s: string) => { if (!s.trim()) return null; const v = parseFloat(s.replace(',', '.')); return Number.isNaN(v) ? null : v; };
const depthLabel = (s: SieveSample) => `${fmt(s.depthFrom, 1)} – ${fmt(s.depthTo, 1)}`;

/**
 * Etykiety 9 frakcji sitowych, od najdrobniejszej. Zestaw sit jest stały i ten sam co
 * SieveCalculator.FR_LABELS po stronie backendu — trzymamy go w jednym miejscu, bo wcześniej
 * był zduplikowany w dwóch komponentach tego pliku.
 */
const FR_LABELS = ['< 0,063', '0,063–0,125', '0,125–0,25', '0,25–0,5', '0,5–1,0', '1,0–2,0', '2,0–4,0', '4,0–8,0', '8,0–16,0'];

type Tab = 'samples' | 'analysis' | 'compare' | 'report';

export function SieveAnalysisDetailPage() {
  const { id } = useParams();
  const projectId = Number(id);
  const navigate = useNavigate();

  const [project, setProject] = useState<SieveProject | null>(null);
  const [meta, setMeta] = useState<SieveMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('samples');
  const [sampleIdx, setSampleIdx] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get(projectId), api.meta()])
      .then(([p, m]) => { setProject(p); setMeta(m); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  }, [projectId]);

  const samples = project?.samples ?? [];
  const clampedIdx = Math.min(sampleIdx, Math.max(0, samples.length - 1));
  const active = samples[clampedIdx];

  const openAnalysis = (idx: number) => { setSampleIdx(idx); setTab('analysis'); };

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;
  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!project) return null;

  return (
    <div className="max-w-[1240px]">
      <button onClick={() => navigate('/analiza-sitowa')} className="text-[13px] text-water-600 hover:text-water-700 font-semibold mb-3">
        ← Projekty
      </button>

      <header className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1>{project.name}</h1>
            <span className={'pill ' + (project.status === 'DONE' ? 'pill-green' : project.status === 'DRAFT' ? 'pill-gray' : 'pill-water')}>
              {project.statusName}
            </span>
          </div>
          <div className="flex gap-x-5 gap-y-1 flex-wrap text-[12px] text-ink-500 mt-1">
            {project.intakeId != null
              ? metaLink('Ujęcie', project.intakeName || project.intakeLabel, `/intakes/${project.intakeId}`)
              : metaChip('Ujęcie', project.intakeName)}
            {project.boreholeId != null
              ? metaLink('Otwór', project.well || project.boreholeLabel, `/otwory/${project.boreholeId}/karta`)
              : metaChip('Otwór', project.well)}
            {metaChip('Miejscowość', project.town)}
            {metaChip('Data', project.studyDate)}
            {metaChip('Wykonał', project.performedBy)}
            {project.dvgwIntermittent && <span className="text-amber-600 font-semibold">DVGW: praca przerywana (Fg=5)</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5" /> Dodaj próbkę
            </button>
          )}
          {canEdit && (
            <button onClick={() => setShowEdit(true)} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5" /> Edytuj projekt
            </button>
          )}
          <button onClick={() => setTab('report')} className="btn btn-secondary btn-sm">
            <Icon name="download" className="w-3.5 h-3.5" /> Raport PDF
          </button>
          {canEdit && (
            <button onClick={() => onDeleteProject(project, navigate)} className="btn btn-ghost btn-sm text-ink-400 hover:text-red-500">
              <Icon name="x" className="w-3.5 h-3.5" /> Usuń
            </button>
          )}
        </div>
      </header>

      <div className="flex gap-1 mb-4 border-b border-ink-100">
        {([['samples', 'Próbki'], ['analysis', 'Analiza próbki'], ['compare', 'Porównanie i decyzja'], ['report', 'Raport']] as [Tab, string][]).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className={'px-3 py-2 text-[13px] font-semibold border-b-2 -mb-px ' +
              (tab === t ? 'border-water-500 text-water-700' : 'border-transparent text-ink-400 hover:text-ink-700')}>
            {label}
          </button>
        ))}
      </div>

      {samples.length === 0 && tab !== 'report' && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak próbek. Kliknij „Dodaj próbkę", aby wprowadzić masy frakcji ręcznie lub wczytać ze zdjęcia (AI).
        </div></div>
      )}

      {samples.length > 0 && tab === 'samples' && (
        <SamplesTab project={project} onAnalyze={openAnalysis}
          onDeleteSample={(sid) => api.deleteSample(project.id, sid).then(p => {
            setProject(p);
            setSampleIdx(i => Math.min(i, Math.max(0, p.samples.length - 1)));
          }).catch(e => alert('Błąd: ' + e.message))} />
      )}

      {samples.length > 0 && tab === 'analysis' && active && meta && (
        <AnalysisTab key={active.id} project={project} sample={active} idx={clampedIdx}
          onProject={setProject} onIdx={setSampleIdx} />
      )}

      {samples.length > 0 && tab === 'compare' && active && meta && (
        <CompareTab key={active.id} project={project} sample={active} idx={clampedIdx} meta={meta}
          onIdx={setSampleIdx} onProject={setProject} />
      )}

      {tab === 'report' && (
        <ReportTab project={project} onProject={setProject} />
      )}

      {showAdd && (
        <SampleModal project={project} onClose={() => setShowAdd(false)}
          onSaved={(p) => { setShowAdd(false); setProject(p); }} />
      )}

      {showEdit && (
        <SieveProjectFormModal project={project} onClose={() => setShowEdit(false)}
          onSaved={(p) => { setShowEdit(false); setProject(p); }} />
      )}
    </div>
  );
}

function metaChip(k: string, v?: string | null) {
  if (!v) return null;
  return <span><span className="text-ink-400 uppercase tracking-wide text-[10px]">{k}:</span> <span className="font-semibold text-ink-700">{v}</span></span>;
}

/**
 * Chip z przejściem do powiązanego rekordu portalu. Integracja musi działać w obie strony:
 * karta ujęcia i karta otworu prowadzą do analiz, więc analiza ma prowadzić z powrotem —
 * inaczej geolog, który wszedł tu z listy, nie ma jak sprawdzić konstrukcji filtra.
 */
function metaLink(k: string, v: string | null | undefined, to: string) {
  if (!v) return null;
  return (
    <span>
      <span className="text-ink-400 uppercase tracking-wide text-[10px]">{k}:</span>{' '}
      <NavLink to={to} className="font-semibold text-water-600 hover:underline">{v}</NavLink>
    </span>
  );
}

async function onDeleteProject(project: SieveProject, navigate: ReturnType<typeof useNavigate>) {
  if (!confirm(`Usunąć projekt „${project.name}" wraz ze wszystkimi próbkami? Tej operacji nie można cofnąć.`)) return;
  try { await api.remove(project.id); navigate('/analiza-sitowa'); }
  catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
}

// ── Zakładka: Próbki ─────────────────────────────────────────────────────────────

function SamplesTab({ project, onAnalyze, onDeleteSample }: {
  project: SieveProject; onAnalyze: (idx: number) => void; onDeleteSample: (sid: number) => void;
}) {
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');
  return (
    <div className="card">
      <div className="card-body !p-0">
        <table className="table-hw">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>Głębokość [m]</th>
              <th className="text-right">Σ masa [g]</th>
              <th className="text-right">d₁₀</th>
              <th className="text-right">d₅₀</th>
              <th className="text-right">d₆₀</th>
              <th className="text-right">U</th>
              <th className="text-center">Sufozja</th>
              <th>PN — obsypka</th>
              <th>DVGW — obsypka</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {project.samples.map((s, k) => {
              const r = s.result;
              return (
                <tr key={s.id} onClick={() => onAnalyze(k)}>
                  <td className="text-ink-400 font-semibold">{k + 1}</td>
                  <td className="font-semibold font-mono tabular-nums">{depthLabel(s)}</td>
                  <td className="text-right font-mono tabular-nums text-ink-500">{fmt(r.tot, 0)}</td>
                  <td className="text-right font-mono tabular-nums">{fmt(r.d10, 2)}</td>
                  <td className="text-right font-mono tabular-nums font-semibold">{fmt(r.d50, 2)}</td>
                  <td className="text-right font-mono tabular-nums">{fmt(r.d60, 2)}</td>
                  <td className="text-right font-mono tabular-nums">{fmt(r.cu, 1)}</td>
                  <td className="text-center">{r.suff ? <span className="text-amber-600" title="Grunt sufozyjny">⚠</span> : '—'}</td>
                  <td className="text-water-700 font-semibold">{r.tot === 0 ? '–' : (r.pn.type === 'no' ? 'filtr bez obsypki' : (r.pn.fracLabel || '–'))}</td>
                  <td className="text-green-600 font-semibold">{r.dvgw.natural ? 'filtr naturalny' : (r.dvgw.groupLabel || '–')}</td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={(e) => { e.stopPropagation(); onAnalyze(k); }} className="btn btn-ghost btn-sm text-water-600">Analiza →</button>
                      {canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); if (confirm('Usunąć próbkę ' + depthLabel(s) + ' m?')) onDeleteSample(s.id); }}
                          className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń próbkę">
                          <Icon name="x" className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Zakładka: Analiza próbki (edytowalne masy, krzywa, parametry) ──────────────────

function AnalysisTab({ project, sample, idx, onProject, onIdx }: {
  project: SieveProject; sample: SieveSample; idx: number;
  onProject: (p: SieveProject) => void; onIdx: (i: number) => void;
}) {
  const [massStrs, setMassStrs] = useState<string[]>(sample.masses.map(m => String(m).replace('.', ',')));
  const [fromStr, setFromStr] = useState(sample.depthFrom == null ? '' : String(sample.depthFrom).replace('.', ','));
  const [toStr, setToStr] = useState(sample.depthTo == null ? '' : String(sample.depthTo).replace('.', ','));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<{ masses: string[]; f: string; t: string } | null>(null);
  // Numer żądania: przy szybkim pisaniu odpowiedzi potrafią wrócić nie po kolei, a każda
  // niesie CAŁY projekt. Bez tego licznika starsza odpowiedź nadpisywała nowszy stan.
  const reqSeq = useRef(0);
  const lastApplied = useRef(0);

  const r = sample.result;

  const doSave = (masses: string[], f: string, t: string) => {
    const myReq = ++reqSeq.current;
    setSaving(true);
    setSaveErr(null);
    api.updateSample(project.id, sample.id, { masses: masses.map(num), depthFrom: numOrNull(f), depthTo: numOrNull(t) })
      .then(p => {
        if (myReq < lastApplied.current) return;   // spóźniona odpowiedź — ignorujemy
        lastApplied.current = myReq;
        onProject(p);
      })
      .catch(e => setSaveErr(e?.message ?? String(e)))
      .finally(() => { if (myReq === reqSeq.current) setSaving(false); });
  };
  const scheduleSave = (masses: string[], f: string, t: string) => {
    pending.current = { masses, f, t };
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { pending.current = null; doSave(masses, f, t); }, 650);
  };
  // Flush niezapisanej edycji przy odmontowaniu (np. zmiana próbki key=active.id) — nie gub danych.
  useEffect(() => () => {
    window.clearTimeout(timer.current);
    const p = pending.current;
    if (p) {
      pending.current = null;
      api.updateSample(project.id, sample.id, { masses: p.masses.map(num), depthFrom: numOrNull(p.f), depthTo: numOrNull(p.t) })
        .then(onProject).catch(() => { /* flush best-effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setMass = (j: number, v: string) => { const m = massStrs.slice(); m[j] = v; setMassStrs(m); scheduleSave(m, fromStr, toStr); };

  const markers: CurveMarker[] = [
    r.d10 != null ? { d: r.d10, label: 'd₁₀ = ' + fmt(r.d10), color: '#b45309' } : null,
    r.dg != null ? { d: r.dg, label: 'dg = ' + fmt(r.dg), color: '#7c3aed' } : null,
    r.d50 != null ? { d: r.d50, label: 'd₅₀ = ' + fmt(r.d50), color: '#0f7fc0' } : null,
    r.d60 != null ? { d: r.d60, label: 'd₆₀ = ' + fmt(r.d60), color: '#64748b' } : null,
  ].filter(Boolean) as CurveMarker[];

  const params: [string, string][] = [
    ['d₁₀ [mm]', fmt(r.d10, 3)], ['d₁₅ [mm]', fmt(r.d15, 3)], ['d₂₀ [mm]', fmt(r.d20, 3)], ['d₃₀ [mm]', fmt(r.d30, 3)],
    ['d₅₀ [mm]', fmt(r.d50, 3)], ['d₆₀ [mm]', fmt(r.d60, 3)], ['d₈₅ [mm]', fmt(r.d85, 3)], ['dg (DVGW) [mm]', fmt(r.dg, 3)],
    ['U = d₆₀/d₁₀', fmt(r.cu, 2)], ['Cc', fmt(r.cc, 2)], ['Fg (DVGW)', fmt(r.dvgw.fg, 2)],
  ];

  return (
    <div>
      {/* nawigacja próbek */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <button onClick={() => onIdx((idx + project.samples.length - 1) % project.samples.length)} className="btn btn-secondary btn-sm">← poprz.</button>
            <button onClick={() => onIdx((idx + 1) % project.samples.length)} className="btn btn-secondary btn-sm">nast. →</button>
          </div>
          <div className="text-[15px] font-semibold">Próbka {depthLabel(sample)} m</div>
          <span className={'pill ' + (r.suff ? 'pill-amber' : 'pill-green')}>{r.suff ? 'sufozja ⚠' : 'niesufozyjny'}</span>
          {saving && <span className="text-[11px] text-ink-400">zapisywanie…</span>}
          {saveErr && <span className="text-[11px] text-red-500 font-semibold">Błąd zapisu: {saveErr}</span>}
        </div>
        <div className="flex items-center gap-2 text-[13px] bg-white border border-ink-100 rounded-md px-3 py-1.5">
          <span className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Grunt (PN-EN ISO 14688)</span>
          <span className="text-[17px] font-bold text-water-700">{r.soilSym}</span>
          <span className="font-semibold text-ink-700">{r.soilName}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,420px)_1fr] gap-4 items-start">
        {/* lewa: analiza sitowa + kf */}
        <div className="grid gap-4 min-w-0">
          <div className="card min-w-0">
            <div className="card-body !p-0">
              <div className="px-4 pt-3 pb-1 font-semibold text-[14px]">Analiza sitowa <span className="text-[11px] text-ink-400 font-normal">{canEdit ? '(edytuj masy — przeliczy się automatycznie)' : '(podgląd — brak uprawnienia SIEVE_EDIT)'}</span></div>
              <div className="flex gap-2 px-4 py-2">
                <label className="field flex-1"><span className="field-label">Głębokość od [m]</span>
                  <input disabled={!canEdit} value={fromStr} onChange={e => { setFromStr(e.target.value); scheduleSave(massStrs, e.target.value, toStr); }} className="input" /></label>
                <label className="field flex-1"><span className="field-label">do [m]</span>
                  <input disabled={!canEdit} value={toStr} onChange={e => { setToStr(e.target.value); scheduleSave(massStrs, fromStr, e.target.value); }} className="input" /></label>
              </div>
              <div className="overflow-x-auto">
                <table className="table-hw [&_th]:!px-2.5 [&_td]:!px-2.5">
                  <thead><tr><th>Frakcja [mm]</th><th className="text-right">Masa [g]</th><th className="text-right">Udział [%]</th><th className="text-right">Przech. [%]</th></tr></thead>
                  <tbody>
                    {FR_LABELS.map((lab, j) => (
                      <tr key={j} className="!cursor-default hover:!bg-transparent">
                        <td className="font-mono tabular-nums text-ink-600 whitespace-nowrap">{lab}</td>
                        <td className="text-right"><input disabled={!canEdit} value={massStrs[j] ?? ''} onChange={e => setMass(j, e.target.value)}
                          className="input !py-1 !px-2 w-[64px] text-right tabular-nums ml-auto" /></td>
                        <td className="text-right font-mono tabular-nums text-ink-500">{fmt(r.pct[j], 1)}</td>
                        <td className="text-right font-mono tabular-nums font-semibold">{fmt(r.pass[j], 1)}</td>
                      </tr>
                    ))}
                    <tr className="!cursor-default hover:!bg-transparent font-semibold">
                      <td>Suma</td><td className="text-right font-mono tabular-nums">{fmt(r.tot, 0)}</td><td className="text-right">100,0</td><td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <KfCard project={project} sample={sample} onProject={onProject} />
        </div>

        {/* prawa: krzywa + parametry + wynik */}
        <div className="grid gap-4 min-w-0">
          <div className="card"><div className="card-body">
            <div className="font-semibold text-[14px] mb-2">Krzywa uziarnienia</div>
            <GrainCurveChart pass={r.pass} markers={markers} />
          </div></div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="card min-w-0"><div className="card-body">
              <div className="font-semibold text-[14px] mb-2">Parametry charakterystyczne</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                {params.map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-dashed border-ink-100 py-1">
                    <span className="text-ink-400 font-semibold">{k}</span><span className="font-bold font-mono tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
              <div className={'mt-3 rounded-lg px-3 py-2 text-[12px] font-semibold border ' +
                (r.suff ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-green-50 border-green-200 text-green-700')}>
                {r.suff ? '⚠ Podejrzenie gruntu sufozyjnego — ≥ 2 maksima rozkładu uziarnienia (DVGW W 113)' : '✓ Brak cech sufozji — jedno maksimum rozkładu'}
              </div>
            </div></div>

            <div className="card min-w-0"><div className="card-body grid gap-3">
              <div className="font-semibold text-[14px]">Wynik doboru</div>
              <div className="border border-water-200 bg-water-50 rounded-lg px-3 py-2">
                <div className="text-[10px] font-bold text-water-700 uppercase tracking-wide">PN-G-02318:1994</div>
                <div className="text-[14px] font-bold mt-0.5">
                  {r.tot === 0 ? 'brak danych'
                    : r.pn.resFrac === '—' ? (r.pn.type === 'no' ? 'filtr bez obsypki' : 'filtr z obsypką')
                    : (r.pn.resFrac + ' mm')}
                </div>
                <div className="text-[12px] text-ink-600">Szczelina: {r.pn.resSlot} mm</div>
              </div>
              <div className="border border-green-200 bg-green-50 rounded-lg px-3 py-2">
                <div className="text-[10px] font-bold text-green-700 uppercase tracking-wide">DVGW W 113 (A)</div>
                <div className="text-[14px] font-bold mt-0.5">{r.dvgw.natural ? 'filtr naturalny (obsypka zbędna)' : (r.dvgw.resFrac + ' mm')}</div>
                {!r.dvgw.natural && <div className="text-[12px] text-ink-600">Szczelina: {r.dvgw.resSlot} mm</div>}
              </div>
            </div></div>
          </div>

          {/* przewodnik metodyczny */}
          <div className="card"><div className="card-body">
            <button onClick={() => setShowGuide(g => !g)} className="flex items-center gap-2 font-semibold text-[14px] w-full text-left">
              <Icon name="chevron" className={'w-3.5 h-3.5 transition-transform ' + (showGuide ? 'rotate-90' : '')} />
              Przewodnik metodyczny — krok po kroku
            </button>
            {showGuide && (
              <div className="mt-2 grid gap-4">
                {/* Część zmienna — kroki wyliczone przez backend dla TEJ próbki. */}
                <div className="grid gap-2 text-[12.5px] text-ink-700 leading-relaxed">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-ink-400">
                    Krok po kroku — próbka {depthLabel(sample)} m
                  </div>
                  {r.guide.map((g, i) => <div key={i} className="border-b border-dashed border-ink-100 pb-2">{g}</div>)}
                </div>
                {/* Część stała — tło metodyczne i tabele referencyjne obu norm. */}
                <MethodGuide />
              </div>
            )}
          </div></div>
        </div>
      </div>
    </div>
  );
}

// ── Karta: współczynnik filtracji k (3 metody + dobór metody do raportu) ──────────
//
// Metoda jest dobierana AUTOMATYCZNIE wg zakresów stosowania (przy kwalifikacji wygrywa USBSC,
// dalej Beyer zalecany w DVGW W 113, dalej Hazen). Geolog może ją nadpisać dla konkretnej próbki
// — wtedy do raportu i do tabeli zbiorczej idzie jego wybór, a nie automat.

function KfCard({ project, sample, onProject }: {
  project: SieveProject; sample: SieveSample; onProject: (p: SieveProject) => void;
}) {
  const r = sample.result;
  const kf = r.kf;
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');
  const [busy, setBusy] = useState(false);

  const [methodErr, setMethodErr] = useState<string | null>(null);

  // Pusty string wysyłamy CELOWO: backend traktuje null jako „nie zmieniaj", więc tylko ''
  // realnie kasuje nadpisanie i wraca do doboru automatycznego.
  const setMethod = (v: string) => {
    setBusy(true);
    setMethodErr(null);
    api.updateDecision(project.id, sample.id, { kfMethod: v as SieveKfMethod | '' })
      .then(onProject)
      .catch(e => setMethodErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  const methods: { id: SieveKfMethod; name: string; text: string; md: string; fits: boolean }[] = [
    { id: 'beyer', name: 'Beyer', text: r.kfBeyerText, md: r.kfBeyerMd, fits: kf.fitsBeyer },
    { id: 'hazen', name: 'Hazen', text: r.kfHazenText, md: r.kfHazenMd, fits: kf.fitsHazen },
    { id: 'usbsc', name: 'USBSC', text: r.kfUsbscText, md: r.kfUsbscMd, fits: kf.fitsUsbsc },
  ];

  return (
    <div className="card"><div className="card-body">
      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
        <div className="font-semibold text-[14px]">Współczynnik filtracji k [m/s]</div>
        <div className="text-[11px] text-ink-400">
          do raportu: <span className="font-bold text-ink-700">{kf.name}</span>
          {' · '}{kf.auto ? 'dobrana automatycznie' : 'wybrana ręcznie'}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {methods.map(m => {
          const active = kf.method === m.id;
          return (
            <div key={m.id}
                 className={'rounded-lg px-3 py-2 border ' +
                   (active ? 'bg-water-50 border-water-400' : 'bg-ink-50 border-ink-100')}>
              <div className="text-[10px] uppercase tracking-wide text-ink-400 font-bold flex items-center gap-1">
                {m.name}
                {!m.fits && <span title="poza zakresem stosowania" className="text-amber-600">⚠</span>}
              </div>
              <div className="text-[15px] font-bold font-mono tabular-nums">{m.text}</div>
              <div className="text-[11px] font-semibold text-water-700 font-mono">{m.md}</div>
            </div>
          );
        })}
      </div>
      <label className="field mt-3"><span className="field-label">Metoda do raportu</span>
        <select className="input" disabled={busy || !canEdit} value={sample.decKfMethod ?? ''}
                onChange={e => setMethod(e.target.value)}>
          <option value="">Automatycznie ({kf.auto ? kf.name : autoNameOf(kf)})</option>
          {methods.map(m => (
            <option key={m.id} value={m.id}>{m.name}{m.fits ? '' : ' — poza zakresem'}</option>
          ))}
        </select>
      </label>
      {methodErr && <div className="mt-2 text-[12px] text-red-500">Błąd: {methodErr}</div>}
      {kf.warn && (
        <div className="mt-2 rounded-lg px-3 py-2 text-[12px] font-semibold bg-amber-50 border border-amber-200 text-amber-700">
          ⚠ Metoda {kf.name} jest poza swoim zakresem stosowania — wartość traktować orientacyjnie.
        </div>
      )}
      <div className="mt-2 text-[11px] text-ink-400 leading-relaxed">
        Zakresy stosowania: Beyer 0,06 ≤ d₁₀ ≤ 0,6 mm i U ≤ 20 · Hazen d₁₀ 0,1–3 mm i U &lt; 5 ·
        USBSC d₂₀ 0,1–3 mm i U &lt; 5.
      </div>
    </div></div>
  );
}

/** Nazwa metody, którą wskazałby automat — do etykiety opcji „Automatycznie (…)". */
function autoNameOf(kf: SieveKf): string {
  if (kf.fitsUsbsc) return 'USBSC';
  if (kf.fitsBeyer) return 'Beyer';
  if (kf.fitsHazen) return 'Hazen';
  return 'Beyer';
}

// ── Karta: kryteria Terzaghiego ───────────────────────────────────────────────────

function TerzaghiCard({ result }: { result: SieveResult }) {
  const rows: [string, SieveTerz | null | undefined][] = [
    ['PN-G-02318:1994', result.terzPn],
    ['DVGW W 113 (A)', result.terzDvgw],
  ];
  if (!result.terzPn && !result.terzDvgw) return null;

  return (
    <div className="card"><div className="card-body">
      <div className="font-semibold text-[14px] mb-2">Kryteria Terzaghiego</div>
      <div className="grid gap-1.5">
        {rows.map(([label, tz]) => (
          <div key={label} className="flex justify-between gap-3 items-baseline border-b border-dashed border-ink-100 pb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-ink-400 shrink-0">{label}</span>
            <span className="text-[12.5px] font-semibold text-right">
              {tz ? tz.text : <span className="text-ink-400 font-normal">— brak obsypki do sprawdzenia</span>}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-ink-400 leading-relaxed">
        Obsypka modelowana jako uziarnienie log-równomierne między granicami frakcji
        (D<sub>x</sub> = a·(b/a)<sup>x/100</sup>) — producent podaje tylko granice, nie pełną krzywą.
        Szczelność D₁₅/d₈₅ ≤ 4 ✓ · przepuszczalność D₁₅/d₁₅ ≥ 5 ✓.
      </div>
    </div></div>
  );
}

// ── Zakładka: Porównanie i decyzja ────────────────────────────────────────────────

function CompareTab({ project, sample, idx, meta, onIdx, onProject }: {
  project: SieveProject; sample: SieveSample; idx: number; meta: SieveMeta;
  onIdx: (i: number) => void; onProject: (p: SieveProject) => void;
}) {
  const r = sample.result;
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');
  const [busy, setBusy] = useState(false);
  const [decErr, setDecErr] = useState<string | null>(null);

  const saveDecision = (patch: SieveDecisionInput) => {
    setBusy(true);
    setDecErr(null);
    api.updateDecision(project.id, sample.id, patch)
      .then(onProject)
      .catch(e => setDecErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  // „–" (półpauza) w resFrac/resSlot oznacza BRAK wyniku, a nie wartość do przepisania —
  // w odróżnieniu od „—" (myślnik), które jest legalną opcją „filtr bez obsypki".
  const val = (x: string) => (x === '–' ? null : x);

  /** Jednoklikowe przyjęcie wyniku normy — przepisuje frakcję i szczelinę do decyzji. */
  const acceptPn = () => saveDecision({
    fraction: val(r.pn.resFrac), slot: val(r.pn.resSlot), basis: 'PN',
  });
  // UWAGA: r.dvgw.resSlot to etykieta z tab. 1 W 113 i zwykle jest ZAKRESEM („1,0 – 1,5") —
  // do decyzji idzie resSlotValue, czyli pojedyncza wartość ze słownika szczelin.
  const acceptDvgw = () => saveDecision({
    fraction: val(r.dvgw.resFrac),
    slot: r.dvgw.natural ? val(r.pn.resSlot) : (r.dvgw.resSlotValue ?? null),
    basis: 'DVGW',
  });

  const bands: CurveBand[] = [];
  if (r.pn.type === 'pack' && r.pn.fracLo != null && r.pn.fracHi != null)
    bands.push({ lo: r.pn.fracLo, hi: r.pn.fracHi, color: 'rgba(29,155,227,0.16)', textColor: '#0f7fc0', label: 'PN: ' + r.pn.fracLabel });
  if (!r.dvgw.natural && r.dvgw.groupLo != null && r.dvgw.groupHi != null)
    bands.push({ lo: r.dvgw.groupLo, hi: r.dvgw.groupHi, color: 'rgba(33,164,70,0.15)', textColor: '#178a3c', label: 'DVGW: ' + r.dvgw.groupLabel });

  const pnSteps: [string, string][] = [
    ['Średnica miarodajna', 'd₅₀ = ' + fmt(r.d50) + ' mm'],
    ['Kryterium doboru', '4 ≤ D/d₅₀ ≤ 6'],
    ['Wynik obliczeniowy', 'D = ' + fmt(r.pn.r0) + ' – ' + fmt(r.pn.r1) + ' mm'],
    // Kryteria stosowania z pkt 2.3.10 — pokazujemy OBA, bo to one rozstrzygają typ filtra.
    ['Kryteria stosowania',
      'z obsypką (U ≤ 3 i d₁₀ ≤ 0,25): ' + (r.pn.withPack ? 'spełnione' : 'niespełnione')
      + ' · bez obsypki (U ≥ 3 i d₁₀ ≥ 0,025): ' + (r.pn.noPack ? 'spełnione' : 'niespełnione')],
    ['Typ filtra', r.tot === 0 ? 'brak danych' : (r.pn.type === 'no' ? 'filtr bez obsypki' : 'filtr z obsypką')],
    ['Frakcja alternatywna', r.pn.altLabel ? r.pn.altLabel + ' mm' : '–'],
    ['Szczelina', r.pn.type === 'pack' ? ('W ≤ 0,8·Dmin = ' + fmt(r.pn.wMax) + ' mm') : ('d₅₀/3 ≤ W ≤ d₅₀')],
    ['Grubość obsypki [mm]', r.pn.thick ? (r.pn.thick + ' – 200') : '–'],
  ];
  const dvSteps: [string, string][] = [
    ['Średnica miarodajna', 'dg = ' + fmt(r.dg) + ' mm'],
    ['Kryterium doboru', 'Fg = ' + fmt(r.dvgw.fg, 2)],
    ['Wynik obliczeniowy', 'Ds = dg·Fg = ' + fmt(r.dvgw.ds) + ' mm'],
    ['CU = d₆₀/d₁₀', fmt(r.cu, 2)],
    ['Typ filtra', r.dvgw.natural ? 'grunt gruboziarnisty — obsypka zbędna' : 'filtr z obsypką'],
    ['Szczelina', r.dvgw.natural ? '–' : ('DIN 4924: ' + (r.dvgw.slotTxt || '–'))],
    ['Grubość obsypki [mm]', r.dvgw.natural ? '–' : ('≥ ' + r.dvgw.thick)],
  ];

  return (
    <div>
      {/* zakładki głębokości */}
      <div className="flex gap-2 flex-wrap mb-3">
        {project.samples.map((s, k) => (
          <button key={s.id} onClick={() => onIdx(k)}
            className={'btn btn-sm font-mono tabular-nums ' + (k === idx ? 'btn-primary' : 'btn-secondary')}>
            {depthLabel(s)}{s.decSaved ? ' ✓' : ''}
          </button>
        ))}
      </div>

      {/* banner zgodności */}
      <div className={'rounded-lg px-4 py-2.5 text-[13.5px] font-semibold mb-4 border ' +
        (r.concord && !r.suff ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700')}>
        {r.concord ? '✓ Wyniki obu norm są zbieżne' : '⚠ Wyniki norm różnią się — decyzja geologa rozstrzyga'}
        {r.suff && ' · ⚠ Podejrzenie gruntu sufozyjnego (DVGW W 113)'}
      </div>

      {/* Ds poza tabelą DIN 4924 — wprost, zamiast milczącego braku grupy */}
      {r.dvgwOutOfTableMessage && (
        <div className="rounded-lg px-4 py-2.5 text-[13px] font-semibold mb-4 border bg-amber-50 border-amber-200 text-amber-700">
          ⚠ {r.dvgwOutOfTableMessage}
        </div>
      )}

      {/* Filtr z obsypką, ale W ≤ 0,8·Dmin wypada poniżej najwęższej szczeliny PVC-U */}
      {r.tot > 0 && r.pn.type === 'pack' && r.pn.fracLo != null && r.pn.slot == null && (
        <div className="rounded-lg px-4 py-2.5 text-[13px] font-semibold mb-4 border bg-amber-50 border-amber-200 text-amber-700">
          ⚠ W ≤ 0,8·Dmin = {fmt(r.pn.wMax)} mm — poniżej najwęższej znormalizowanej szczeliny PVC-U
          (0,5 mm wg PN-G-02323:2011). Dobór szczeliny wymaga decyzji geologa: grubsza obsypka
          albo filtr siatkowy.
        </div>
      )}

      {/* Szczelina bez obsypki poza zakresem znormalizowanych szczelin PVC-U */}
      {r.pn.type === 'no' && r.pn.slotNoNone && (
        <div className="rounded-lg px-4 py-2.5 text-[13px] font-semibold mb-4 border bg-amber-50 border-amber-200 text-amber-700">
          ⚠ Brak znormalizowanej szczeliny PVC-U (min. 0,5 mm) w przedziale d₅₀/3 – d₅₀ —
          grunt zbyt drobny dla filtra bez obsypki; konieczna obsypka lub filtr siatkowy.
        </div>
      )}

      {/* karty PN vs DVGW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <StandardCard title="🇵🇱 Norma polska — PN-G-02318:1994" sub="PN-G-02318:1994 · PN-88/B-06715 · PN-G-02323:2011"
          accent="#1d9be3" steps={pnSteps} resFrac={r.pn.resFrac} resSlot={r.pn.resSlot} resColor="#0f7fc0" />
        <StandardCard title="🇩🇪 Wytyczne niemieckie — DVGW W 113 (A)" sub="DVGW W 113 (A) 2024 · DIN 4924 · DVGW W 123"
          accent="#21a446" steps={dvSteps} resFrac={r.dvgw.resFrac} resSlot={r.dvgw.resSlot} resColor="#178a3c" />
      </div>

      {/* krzywa z pasmami obsypki */}
      <div className="card mb-4"><div className="card-body">
        <div className="flex gap-4 flex-wrap mb-2 text-[12px] font-semibold">
          <span className="text-water-600">━ Krzywa uziarnienia</span>
          <span className="text-water-700"><span className="inline-block w-3 h-3 align-[-2px]" style={{ background: 'rgba(29,155,227,0.18)', border: '1px solid #9fd4f2' }} /> obsypka wg PN</span>
          <span className="text-green-600"><span className="inline-block w-3 h-3 align-[-2px]" style={{ background: 'rgba(33,164,70,0.16)', border: '1px solid #b5e3c6' }} /> obsypka wg DVGW</span>
        </div>
        <GrainCurveChart pass={r.pass} bands={bands} width={840} height={420} />
        <div className="grid gap-4 mt-4 md:grid-cols-2">
          <SlotScale result={r} decisionSlot={sample.decSlot} />
          <PackScale result={r} />
        </div>
      </div></div>

      <div className="mb-4"><TerzaghiCard result={r} /></div>

      {/* decyzja geologa */}
      <div className="card"><div className="card-body">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="font-semibold text-[15px]">✍ Decyzja geologa — interwał {depthLabel(sample)} m</div>
          <div className="flex items-center gap-3 flex-wrap">
            {canEdit && (
              <div className="flex gap-1">
                <button disabled={busy} onClick={acceptPn} className="btn btn-secondary btn-sm" title="Przepisz wynik PN do decyzji">
                  Przyjmij PN
                </button>
                <button disabled={busy} onClick={acceptDvgw} className="btn btn-secondary btn-sm" title="Przepisz wynik DVGW do decyzji">
                  Przyjmij DVGW
                </button>
              </div>
            )}
            <label className="flex items-center gap-2 text-[13px] font-semibold text-ink-600 cursor-pointer">
              <input type="checkbox" disabled={!canEdit} checked={sample.decInclude}
                onChange={e => saveDecision({ include: e.target.checked })} className="w-4 h-4 accent-water-500" />
              Interwał ujęty filtrem studni
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label className="field"><span className="field-label">Obsypka (frakcja) [mm]</span>
            <SearchableSelect disabled={!canEdit} value={sample.decFraction ?? ''} onChange={v => saveDecision({ fraction: v })}>
              {meta.fractionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </SearchableSelect>
          </label>
          <label className="field"><span className="field-label">Szczelina [mm]</span>
            <SearchableSelect disabled={!canEdit} value={sample.decSlot ?? ''} onChange={v => saveDecision({ slot: v })}>
              {meta.slotOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </SearchableSelect>
          </label>
          <label className="field"><span className="field-label">Podstawa</span>
            <SearchableSelect disabled={!canEdit} value={sample.decBasis} onChange={v => saveDecision({ basis: v as SieveDecisionBasis })}>
              {meta.bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </SearchableSelect>
          </label>
        </div>
        <label className="field mb-3"><span className="field-label">Uwagi</span>
          <textarea disabled={!canEdit} defaultValue={sample.decNote ?? ''} onBlur={e => saveDecision({ note: e.target.value })} className="textarea" rows={2}
            placeholder="np. dobór ostrożniejszy z uwagi na sufozję…" />
        </label>
        <div className="flex items-center justify-end gap-3 flex-wrap">
          {decErr && <span className="text-[12px] text-red-500 mr-auto">Błąd: {decErr}</span>}
          {!canEdit && <span className="text-[12px] text-ink-400 mr-auto">Podgląd — brak uprawnienia SIEVE_EDIT.</span>}
          {sample.decSaved && <span className="text-green-600 text-[13px] font-semibold">✓ Zatwierdzono</span>}
          <button disabled={busy || !canEdit} onClick={() => saveDecision({ saved: true })} className="btn btn-primary">
            <Icon name="check" className="w-3.5 h-3.5" /> Zatwierdź decyzję
          </button>
        </div>
      </div></div>
    </div>
  );
}

function StandardCard({ title, sub, accent, steps, resFrac, resSlot, resColor }: {
  title: string; sub: string; accent: string; steps: [string, string][]; resFrac: string; resSlot: string; resColor: string;
}) {
  return (
    <div className="bg-white border border-ink-100 rounded-[10px] shadow-sm overflow-hidden flex flex-col" style={{ borderTop: `4px solid ${accent}` }}>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[15px] font-bold">{title}</div>
        <div className="text-[11px] text-ink-400 font-semibold mt-0.5 mb-3">{sub}</div>
        <div className="flex flex-col flex-1">
          {steps.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 py-1.5 border-b border-dashed border-ink-100 text-[13px]">
              <span className="text-ink-400 font-semibold">{k}</span>
              <span className="font-bold text-right font-mono tabular-nums">{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg px-3 py-2 flex justify-between gap-3 flex-wrap" style={{ background: '#f6fafc', border: '1px solid #e3eaee' }}>
          <div><div className="text-[10px] font-bold uppercase" style={{ color: resColor }}>Frakcja obsypki [mm]</div><div className="text-[17px] font-bold font-mono tabular-nums">{resFrac}</div></div>
          <div className="text-right"><div className="text-[10px] font-bold uppercase" style={{ color: resColor }}>Szczelina [mm]</div><div className="text-[17px] font-bold font-mono tabular-nums">{resSlot}</div></div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: dodawanie próbki (ręcznie lub ze zdjęcia AI) ────────────────────────────

function SampleModal({ project, onClose, onSaved }: {
  project: SieveProject; onClose: () => void; onSaved: (p: SieveProject) => void;
}) {
  const [massStrs, setMassStrs] = useState<string[]>(Array(9).fill(''));
  const [fromStr, setFromStr] = useState('');
  const [toStr, setToStr] = useState('');
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki

  const readFile = async (file: File) => {
    // Limit 100 MB — blokuj przed wysyłką
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    setReading(true); setErr(null); setInfo(null); setUploadPct(0);
    try {
      const ex = await api.extract(file, undefined, p => setUploadPct(p.percent));
      setMassStrs(ex.masses.map(m => (m ? String(m).replace('.', ',') : '')));
      if (ex.depthFrom != null) setFromStr(String(ex.depthFrom).replace('.', ','));
      if (ex.depthTo != null) setToStr(String(ex.depthTo).replace('.', ','));
      if (ex.provider === 'mock') {
        setErr('⚠ AI działa w trybie MOCK — to nie jest odczyt Twojego zdjęcia, tylko dane przykładowe. '
          + 'Skonfiguruj model (Claude lub Gemini) i klucz API w panelu: Administracja → Konfiguracja.');
      } else {
        setInfo(`Odczytano zdjęcie (${ex.provider}${ex.method ? ', ' + ex.method : ''}). Sprawdź i popraw wartości.`);
      }
    } catch (e) { setErr('Odczyt nie powiódł się: ' + (e instanceof Error ? e.message : String(e))); }
    finally { setReading(false); setUploadPct(null); }
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const p = await api.addSample(project.id, { masses: massStrs.map(num), depthFrom: numOrNull(fromStr), depthTo: numOrNull(toStr) });
      onSaved(p);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { massStrs, fromStr, toStr }, onClose, busy: busy || reading,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-[620px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">Dodaj próbkę</h2></div>
        <div className="p-5 grid gap-3">
          <div onDrop={onDrop} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
            className={'border-2 border-dashed rounded-lg p-4 text-center text-[13px] ' + (dragOver ? 'border-water-500 bg-water-50' : 'border-ink-200')}>
            {reading ? (
              <div className="flex items-center justify-center gap-2 text-ink-500 py-2">
                <span className="w-4 h-4 border-2 border-water-200 border-t-water-500 rounded-full animate-spin" /> AI odczytuje kartę…
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="text-ink-500">📷 Przeciągnij zdjęcie karty przesiewów lub</div>
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Wybierz plik
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); }} />
                </label>
                <div className="text-[11px] text-ink-400">JPG, PNG, PDF · masy wypełnią się automatycznie do sprawdzenia</div>
              </div>
            )}
          </div>
          {uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie…" compact />}
          {info && <div className="text-[12px] text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">{info}</div>}

          <div className="flex gap-2">
            <label className="field flex-1"><span className="field-label">Głębokość od [m]</span>
              <input value={fromStr} onChange={e => setFromStr(e.target.value)} className="input" /></label>
            <label className="field flex-1"><span className="field-label">do [m]</span>
              <input value={toStr} onChange={e => setToStr(e.target.value)} className="input" /></label>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {FR_LABELS.map((lab, j) => (
              <label key={j} className="field">
                <span className="field-label text-[10px]">{lab} mm</span>
                <input value={massStrs[j] ?? ''} onChange={e => { const m = massStrs.slice(); m[j] = e.target.value; setMassStrs(m); }}
                  className="input !py-1 text-right tabular-nums" placeholder="g" />
              </label>
            ))}
          </div>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="button" onClick={submit} disabled={busy || reading} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5" /> {busy ? 'Dodawanie…' : 'Dodaj próbkę'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Zakładka: Raport ──────────────────────────────────────────────────────────────
//
// Odpowiednik kroku 5 prototypu: wybór sekcji, orientacji, uzupełnienie podpisu geologa
// i daty raportu, podgląd wygenerowanego PDF-a oraz zapis do archiwum portalu.

type SectionKey = 'params' | 'comparison' | 'decisions' | 'methodology';

const SECTION_LABELS: [SectionKey, string][] = [
  ['params', 'Parametry'],
  ['comparison', 'Porównanie norm'],
  ['decisions', 'Decyzje'],
  ['methodology', 'Podstawy metodyczne'],
];

function ReportTab({ project, onProject }: {
  project: SieveProject; onProject: (p: SieveProject) => void;
}) {
  const [opt, setOpt] = useState<SieveReportOptions>({
    params: true, comparison: true, decisions: true, methodology: true, landscape: false,
  });
  const [geologName, setGeologName] = useState(project.geologName ?? '');
  const [geologLic, setGeologLic] = useState(project.geologLic ?? '');
  const [reportDate, setReportDate] = useState(project.reportDate ?? '');
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'SIEVE_EDIT');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Podgląd odświeżamy dopiero na żądanie — każdy render PDF-a to realna praca serwera.
  const [previewNonce, setPreviewNonce] = useState(0);

  // Przypisanie przez zmienny klucz, a nie spread z kluczem obliczanym — ten drugi wzorzec
  // TypeScript w trybie strict potrafi odrzucić (klucz unii literałów gubi typ obiektu).
  const setSection = (key: SectionKey, value: boolean) => setOpt(o => {
    const next = { ...o };
    next[key] = value;
    return next;
  });

  const included = project.samples.filter(s => s.decInclude).length;
  const undecided = project.samples.filter(s => s.decInclude && !s.decSaved).length;

  // Nonce wymusza ponowne pobranie PDF-a po zmianie opcji. Doklejamy właściwym separatorem,
  // zamiast zakładać, że helper zawsze zwróci adres z „?".
  const previewBase = api.reportPdfUrl(project.id, opt);
  const previewUrl = previewBase + (previewBase.includes('?') ? '&' : '?') + `_=${previewNonce}`;

  const saveMeta = () => {
    setSaving(true); setErr(null); setMsg(null);
    api.update(project.id, {
      ...sieveProjectToInput(project),
      geologName: geologName || null,
      geologLic: geologLic || null,
      reportDate: reportDate || null,
    })
      .then(p => { onProject(p); setMsg('Zapisano dane do raportu.'); setPreviewNonce(n => n + 1); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setSaving(false));
  };

  const archive = () => {
    setArchiving(true); setErr(null); setMsg(null);
    api.archiveReport(project.id, opt)
      .then(r => setMsg(r.attached
        ? `Raport zapisany w archiwum jako „${r.filename}"` + (r.note ? ` (${r.note})` : '')
        : `Nie podpięto raportu: ${r.note ?? 'nieznany powód'}`))
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setArchiving(false));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
      <div className="grid gap-4">
        <div className="card"><div className="card-body">
          <div className="font-semibold text-[14px] mb-2">Sekcje w raporcie</div>
          <div className="grid gap-1.5">
            {SECTION_LABELS.map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-water-500"
                  checked={opt[key]}
                  onChange={e => setSection(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
          <div className="font-semibold text-[14px] mt-4 mb-2">Format</div>
          <div className="flex gap-1">
            {([[false, 'Pionowo'], [true, 'Poziomo']] as [boolean, string][]).map(([v, label]) => (
              <button key={label} onClick={() => setOpt(o => ({ ...o, landscape: v }))}
                className={'btn btn-sm ' + (opt.landscape === v ? 'btn-primary' : 'btn-secondary')}>
                {label}
              </button>
            ))}
          </div>
        </div></div>

        <div className="card"><div className="card-body grid gap-3">
          <div className="font-semibold text-[14px]">Podpis i data</div>
          <label className="field"><span className="field-label">Imię i nazwisko geologa</span>
            <input className="input" value={geologName} onChange={e => setGeologName(e.target.value)} />
          </label>
          <label className="field"><span className="field-label">Nr uprawnień</span>
            <input className="input" value={geologLic} onChange={e => setGeologLic(e.target.value)} />
          </label>
          <label className="field"><span className="field-label">Data raportu</span>
            <input type="date" className="input" value={reportDate ?? ''} onChange={e => setReportDate(e.target.value)} />
            <span className="text-[11px] text-ink-400 mt-1">Puste — w raporcie data bieżąca.</span>
          </label>
          <button disabled={saving || !canEdit} onClick={saveMeta} className="btn btn-secondary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5" /> {saving ? 'Zapisywanie…' : 'Zapisz dane do raportu'}
          </button>
        </div></div>

        <div className="card"><div className="card-body grid gap-2">
          <div className="text-[12px] text-ink-500">
            Interwałów ujętych filtrem: <span className="font-bold text-ink-800">{included}</span> z {project.samples.length}
          </div>
          {undecided > 0 && (
            <div className="text-[12px] text-amber-700 font-semibold">
              ⚠ {undecided} {undecided === 1 ? 'interwał ma' : 'interwały mają'} niezatwierdzoną decyzję geologa.
            </div>
          )}
          <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
            <Icon name="download" className="w-3.5 h-3.5" /> Otwórz / pobierz PDF
          </a>
          <button disabled={archiving || !canEdit} onClick={archive} className="btn btn-secondary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5" /> {archiving ? 'Zapisywanie…' : 'Zapisz w archiwum'}
          </button>
          {msg && <div className="text-[12px] text-green-700 font-semibold">{msg}</div>}
          {err && <div className="text-[12px] text-red-500">Błąd: {err}</div>}
        </div></div>
      </div>

      <div className="card"><div className="card-body">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="font-semibold text-[14px]">Podgląd</div>
          <button onClick={() => setPreviewNonce(n => n + 1)} className="btn btn-ghost btn-sm">Odśwież podgląd</button>
        </div>
        <iframe key={previewUrl} src={previewUrl} title="Podgląd raportu"
          className="w-full h-[820px] border border-ink-100 rounded-md bg-white" />
      </div></div>
    </div>
  );
}

